import ArrowButton, {ArrowButtonType} from 'components/atoms/arrowButton';
import Carousel from 'components/molecules/carousel';
import ConferencePerson from 'components/molecules/conferencePerson';
import {useTheme} from 'contexts/ThemeContext';
import {ConferenceUser} from 'models/ConferenceUser';
import React, {useRef, useState} from 'react';
import {
  FlatList,
  LayoutChangeEvent,
  ListRenderItemInfo,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import styleSheet from './styles';

const SHARE_HEIGHT_FOR_PERSON = 102;
const SHARE_MAX_WIDTH_FOR_PERSON = 140;

export interface ConferencePersonListProps {
  shareStream?: ConferenceUser;
  isParticipantsHidden: boolean;
  streams: ConferenceUser[];
}

const ConferencePersonList = (props: ConferencePersonListProps) => {
  const requiredOffset = 15;
  const offsetForEachPerson = 15;

  const theme = useTheme();
  const styles = styleSheet(theme);
  const width = useWindowDimensions().width;
  const flatListRef = useRef<FlatList>(null);

  const [personsForPageNumber, setPersonsForPageNumber] = useState(0);
  const [activePage, setActivePage] = useState(0);

  const [sharingLayout, setSharingLayout] = useState({
    height: 0,
    width: 0,
  });
  const [flatListtLayout, setFlatListLayout] = useState({
    height: 0,
    width: 0,
  });

  const minPersonViewWidth = width / 2;

  const getPageCount = () => {
    return Math.ceil(props.streams.length / personsForPageNumber);
  };

  const renderItem = (itemProps: ListRenderItemInfo<ConferenceUser[]>) => {
    let widthForPerson = 0;
    let heightForPerson = 0;

    if (!!props.shareStream) {
      if (theme.isWebSize) {
        heightForPerson =
          SHARE_HEIGHT_FOR_PERSON - offsetForEachPerson - requiredOffset;

        const calcWidth =
          (flatListtLayout.width -
            offsetForEachPerson * personsForPageNumber -
            requiredOffset) /
          personsForPageNumber;

        widthForPerson =
          calcWidth > SHARE_MAX_WIDTH_FOR_PERSON
            ? SHARE_MAX_WIDTH_FOR_PERSON
            : calcWidth;
      }
    } else {
      if (!theme.isWebSize) {
        widthForPerson = (width - offsetForEachPerson * 2 - requiredOffset) / 2;
        if (props.streams.length == 1) {
          widthForPerson = width - offsetForEachPerson - requiredOffset;
        }

        heightForPerson = widthForPerson;
      } else {
        widthForPerson =
          (flatListtLayout.width - offsetForEachPerson * 4 - requiredOffset) /
          4;
        heightForPerson =
          (flatListtLayout.height - offsetForEachPerson * 2 - requiredOffset) /
          2;

        if (props.streams.length <= 4) {
          heightForPerson =
            flatListtLayout.height - offsetForEachPerson - requiredOffset;

          const calcWidth =
            (flatListtLayout.width -
              offsetForEachPerson * props.streams.length -
              requiredOffset) /
            props.streams.length;

          widthForPerson =
            calcWidth > heightForPerson ? heightForPerson : calcWidth;
        }
      }
    }

    return (
      <View style={[styles.pageContainer, {width: flatListtLayout.width}]}>
        {itemProps.item.map((item, index) => {
          return (
            <ConferencePerson
              key={index.toString()}
              index={index}
              isSharing={!!props.shareStream}
              width={widthForPerson}
              height={heightForPerson}
              user={item}
            />
          );
        })}
      </View>
    );
  };

  const onLayout = (event: LayoutChangeEvent) => {
    setFlatListLayout(event.nativeEvent.layout);

    if (!!props.shareStream) {
      const maxCount = Math.floor(
        (event.nativeEvent.layout.width -
          offsetForEachPerson -
          requiredOffset) /
          SHARE_MAX_WIDTH_FOR_PERSON,
      );

      setPersonsForPageNumber(
        props.streams.length <= maxCount ? props.streams.length : maxCount,
      );

      return;
    }

    if (theme.isWebSize) {
      setPersonsForPageNumber(8);
    } else {
      setPersonsForPageNumber(
        Math.floor(
          event.nativeEvent.layout.height /
            (minPersonViewWidth - offsetForEachPerson * 2 - requiredOffset),
        ) * 2,
      );
    }
  };

  const onPressLeftButton = () => {
    flatListRef.current?.scrollToOffset({
      animated: true,
      offset: activePage - 1,
    });
    setActivePage(activePage - 1);
  };

  const onPressRightButton = () => {
    flatListRef.current?.scrollToIndex({
      animated: true,
      index: activePage + 1,
    });
    setActivePage(activePage + 1);
  };

  const LeftArrow = (
    <View style={styles.leftArrowContainer}>
      <ArrowButton
        isLeft={true}
        type={
          activePage == 0 ? ArrowButtonType.disabled : ArrowButtonType.active
        }
        onPress={onPressLeftButton}
      />
    </View>
  );

  const RightArrow = (
    <View style={styles.rightArrowContainer}>
      <ArrowButton
        isLeft={false}
        type={
          activePage == getPageCount() - 1
            ? ArrowButtonType.disabled
            : ArrowButtonType.active
        }
        onPress={onPressRightButton}
      />
    </View>
  );

  const data: ConferenceUser[][] = [];
  if (props.streams.length > 0 && personsForPageNumber > 0) {
    for (
      let index = 0;
      index < props.streams.length / personsForPageNumber;
      index++
    ) {
      data.push(
        props.streams.slice(
          index * personsForPageNumber,
          index * personsForPageNumber + personsForPageNumber,
        ),
      );
    }
  }

  const isAdditionalPageExist =
    props.streams.length > 0 &&
    personsForPageNumber > 0 &&
    props.streams.length > personsForPageNumber;

  let listContainerStyle: ViewStyle = styles.listContainer;
  if (!!props.shareStream) {
    if (props.isParticipantsHidden) {
      listContainerStyle = styles.listContainerHideHeight;
    } else {
      listContainerStyle = styles.listContainerFixedHeight;
    }
  }

  const List = (
    <View style={listContainerStyle}>
      {theme.isWebSize && isAdditionalPageExist && LeftArrow}
      <FlatList
        ref={flatListRef}
        horizontal={true}
        onLayout={onLayout}
        pagingEnabled={!theme.isWebSize}
        style={styles.list}
        data={data}
        renderItem={renderItem}
        onMomentumScrollEnd={event => {
          setActivePage(event.nativeEvent.contentOffset.x / width);
        }}
        showsHorizontalScrollIndicator={false}
      />
      {theme.isWebSize && isAdditionalPageExist && RightArrow}
    </View>
  );

  const ShareView = !!props.shareStream && (
    <View
      style={[
        styles.shareContainer,
        props.isParticipantsHidden ? styles.shareContainerTopSpacer : {},
      ]}
      onLayout={event => {
        setSharingLayout(event.nativeEvent.layout);
      }}>
      <ConferencePerson
        index={-1}
        isSharing={!!props.shareStream}
        width={sharingLayout.width}
        height={sharingLayout.height}
        user={props.shareStream}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      {!props.shareStream ? List : theme.isWebSize && List}
      {!props.shareStream && isAdditionalPageExist && (
        <Carousel activeElement={activePage} elementsCount={getPageCount()} />
      )}
      {ShareView}
    </View>
  );
};

export default ConferencePersonList;
